/**
 * Feedback Parsing Utilities
 * Extracted from ResultAndRevision.js for better organization
 */

import logger from '../utils/logger';
import { getValidCodesForCategory } from './colorMaps.js';

/**
 * Check if the result is a JSON-based response
 */
export function isJsonResponse(result) {
  try {
    if (typeof result === 'object' && (result._format === 'json' || result._format === 'australian')) {
      return true;
    }
    if (typeof result === 'string') {
      const parsed = JSON.parse(result);
      return parsed._format === 'json' || parsed._format === 'australian';
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Parse JSON response into usable format
 */
export function parseJsonResponse(result) {
  try {
    if (typeof result === 'object' && (result._format === 'json' || result._format === 'australian')) {
      // For Australian format, the structure is now consistent with other JSON formats
      // No need for special annotated_feedback parsing
      return result;
    }
    if (typeof result === 'string') {
      const parsed = JSON.parse(result);
      // After parsing, the structure should be consistent
      return parsed;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * parseLineByLine(text, category)
 *
 * Reads the entire "result" string line-by-line and splits it into paragraphs + feedback.
 */
export function parseLineByLine(text, category) {
  // Ensure text is a string
  if (typeof text !== 'string') {
    logger.warn('parseLineByLine called with non-string value:', typeof text);
    return [];
  }
  
  logger.debug(`parseLineByLine: Processing text for category ${category}, length: ${text.length}`);
  
  // Get valid codes for this category
  const validCodes = getValidCodesForCategory(category);
  logger.debug(`parseLineByLine: Valid codes for category ${category}: ${validCodes}`);

  // We'll create a dynamic regex that checks for {X: ...}, 
  // where X is one of the valid codes for this category
  const isSingleBracket = (line) => {
    const pattern = new RegExp(`^\\{${validCodes}:`, 'i');
    // Check if line starts with a pattern like {P: or if it contains [TEACHER] tag
    return pattern.test(line.trim()) || 
           (line.trim().includes('[TEACHER]') && line.trim().includes('}'));
  };

  const lines = text.split('\n');
  const paragraphs = [];
  let currentParagraph = null;

  for (let rawLine of lines) {
    const lineTrim = rawLine.trim();
    if (!lineTrim) continue;

    if (isSingleBracket(lineTrim)) {
      // This line is feedback
      if (currentParagraph) {
        // Parse the feedback line to extract type and content
        const feedbackMatch = lineTrim.match(new RegExp(`^\\{(${validCodes}):\\s*(.+)\\}$`, 'i'));
        if (feedbackMatch) {
          currentParagraph.feedback.push({
            type: feedbackMatch[1],
            content: feedbackMatch[2]
          });
        } else {
          // Fallback for malformed feedback
          currentParagraph.feedback.push(rawLine);
        }
      }
    } else {
      // Paragraph text or leftover short text
      if (lineTrim.length > 5) {
        if (currentParagraph && currentParagraph.text.trim().length > 0) {
          paragraphs.push(currentParagraph);
          currentParagraph = {
            text: rawLine,
            feedback: [],
            inline_annotations: []
          };
        } else if (currentParagraph) {
          currentParagraph.text = rawLine;
        } else {
          currentParagraph = {
            text: rawLine,
            feedback: [],
            inline_annotations: []
          };
        }
      } else {
        // lineTrim <= 5 => leftover short text
        if (!currentParagraph) {
          currentParagraph = { text: rawLine, feedback: [], inline_annotations: [] };
        } else {
          currentParagraph.text += '\n' + rawLine;
        }
      }
    }
  }
  if (currentParagraph && currentParagraph.text.trim().length > 0) {
    paragraphs.push(currentParagraph);
  }
  
  // Merge short paragraphs (< 150 words, no feedback) with their subsequent paragraph
  for (let i = 0; i < paragraphs.length - 1; i++) {
    const paragraph = paragraphs[i];
    const wordCount = paragraph.text.trim().split(/\s+/).length;
    if (wordCount < 150 && paragraph.feedback.length === 0) {
      // Merge paragraph i into paragraph i+1
      paragraphs[i+1].text = paragraph.text + "\n\n" + paragraphs[i+1].text;
      // Merge feedback and annotations as well
      paragraphs[i+1].feedback = [...paragraph.feedback, ...paragraphs[i+1].feedback];
      paragraphs[i+1].inline_annotations = [...paragraph.inline_annotations, ...paragraphs[i+1].inline_annotations];
      // Remove paragraph i from the array
      paragraphs.splice(i, 1);
      // Step back one index so we don't skip the newly-shifted paragraph
      i--;
    }
  }

  // Process each paragraph to extract inline annotations from {{code: text}} patterns
  paragraphs.forEach(paragraph => {
    const inlineAnnotationRegex = new RegExp(`\\{\\{(${validCodes}):\\s*([^}]+?)\\}\\}`, 'g');
    const annotations = [];
    let cleanText = '';
    let lastIndex = 0;
    let match;
    
    // Process each inline annotation match
    while ((match = inlineAnnotationRegex.exec(paragraph.text)) !== null) {
      // Add text before the annotation to cleanText
      cleanText += paragraph.text.slice(lastIndex, match.index);
      
      // Record the annotation with adjusted position (in the clean text)
      annotations.push({
        type: match[1],
        start: cleanText.length,
        end: cleanText.length + match[2].length,
        comment: `${match[1]}: Highlighted text`
      });
      
      // Add the annotation content (without brackets) to cleanText
      cleanText += match[2];
      
      // Update lastIndex to continue after this match
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text after the last annotation
    cleanText += paragraph.text.slice(lastIndex);
    
    // Update the paragraph with clean text and annotations
    paragraph.text = cleanText;
    paragraph.inline_annotations = annotations;
  });

  logger.debug(`parseLineByLine: Parsed ${paragraphs.length} paragraphs with inline annotations`);
  paragraphs.forEach((p, i) => {
    logger.debug(`Paragraph ${i}: ${p.text.substring(0, 50)}... (${p.inline_annotations.length} annotations, ${p.feedback.length} feedback items)`);
  });

  return paragraphs;
}

/**
 * parseForRevision: remove double-bracket blocks, split on single-bracket
 */
export function parseForRevision(fullText) {
  // Ensure fullText is a string
  if (typeof fullText !== 'string') {
    logger.warn('parseForRevision called with non-string value:', typeof fullText);
    return [];
  }
  
  // If you want to remove the entire "Total Score Calculation" block:
  const scoreIndex = fullText.indexOf('Total Score Calculation');
  if (scoreIndex !== -1) {
    fullText = fullText.slice(0, scoreIndex); 
  }

  // Now remove just the brackets but keep the words:
  let withoutDouble = fullText.replace(/\{\{([PACLMGSE]):\s*(.*?)\}\}/g, '$2');

  // Add a console log to see the text without brackets
  logger.debug("Text after removing double brackets:", withoutDouble.substring(0, 100) + "...");

  // Split on single-bracket comments {P: ...}, capturing them in the result array
  const splitRegex = /(\{[PACLMGSE]:.*?\})/g;
  const parts = withoutDouble.split(splitRegex);

  logger.debug("Parts split by single brackets:", parts.length);

  const segments = [];
  for (let i = 0; i < parts.length; i++) {
    const piece = parts[i];
    if (!piece) continue;

    const trimmed = piece.trim();
    // Is this piece a single-bracket comment block?
    if (/^\{[PACLMGSE]:.*?\}$/.test(trimmed)) {
      segments.push({
        type: 'comment',
        content: trimmed
      });
    } else {
      // It's essay text. Skip if it's too short (e.g. <6 chars) to avoid empty textareas.
      if (trimmed.length < 6) continue;

      segments.push({
        type: 'text',
        content: piece // Keep original whitespace
      });
    }
  }
  
  logger.debug("Parsed segments:", segments.length);
  return segments;
} 